import { FavoriteListingsResponse } from 'apolloClient/types/FavoriteListing';
import { useAuth } from 'components/Auth/AuthProvider';
import { PropertyType } from 'components/UnitsFilters/types';
import React, { useEffect, useMemo, useState } from 'react';
import { useFavorite } from './SavedFavoritesContext';
import FavoriteButtonBase from './FavoriteButtonBase';

type SaveToFavoritesButtonProps = {
  full?: boolean;
  unitId: number;
  type: PropertyType;
  userFavorites?: FavoriteListingsResponse;
  visibility?: string;
  className?: string;
  iconWidth?: number;
  iconHeight?: number;
  desktopIconWidth?: number;
  desktopIconHeight?: number;
  labelClassName?: string;
  darkGold?: boolean;
  gapClassName?: string;
};

const LOCAL_STORAGE_FAVORITES_KEY = 'saveFavoritesTmp';

const SaveToFavoritesButton: React.FC<SaveToFavoritesButtonProps> = ({
  visibility,
  unitId,
  type,
  full,
  userFavorites,
  className,
  iconWidth,
  iconHeight,
  desktopIconWidth,
  desktopIconHeight,
  labelClassName,
  darkGold,
  gapClassName,
}) => {
  const [onSaveAction, setOnSaveAction] = useState(false);

  const { isLogged, openHeaderPopup } = useAuth();

  const { createFavorite, deleteFavorite, getFavorites } = useFavorite();

  const favorite = useMemo(
    () =>
      userFavorites?.data?.find(({ attributes }) => attributes?.type === type),
    [userFavorites, type]
  );

  const isSaved = !!favorite?.id;

  useEffect(() => {
    if (isLogged && onSaveAction) {
      const favoriteData = localStorage.getItem(LOCAL_STORAGE_FAVORITES_KEY);
      if (favoriteData) {
        try {
          createFavorite(JSON.parse(favoriteData));
        } catch (error) {
          console.error('Data is not a JSON string:', favoriteData);
          console.error('Error parsing JSON createFavorite', error);
        }
        localStorage.removeItem(LOCAL_STORAGE_FAVORITES_KEY);
      }
    }
  }, [createFavorite, deleteFavorite, isLogged, onSaveAction]);

  const clickHandler = () => {
    if (!isLogged) {
      localStorage.setItem(
        LOCAL_STORAGE_FAVORITES_KEY,
        JSON.stringify({ type, unit: unitId, alertFrequency: 'Daily' })
      );
      return openHeaderPopup({
        successHandler: () => {
          getFavorites();
          setOnSaveAction(true);
        },
      });
    }

    isSaved
      ? deleteFavorite({ id: favorite?.id, type, unitId })
      : createFavorite({ type, unit: unitId, alertFrequency: 'Daily' });
  };

  return (
    <FavoriteButtonBase
      isSaved={isSaved}
      isLogged={isLogged}
      onClick={clickHandler}
      full={full}
      visibility={visibility}
      className={className}
      iconWidth={iconWidth}
      iconHeight={iconHeight}
      desktopIconWidth={desktopIconWidth}
      desktopIconHeight={desktopIconHeight}
      labelClassName={labelClassName}
      darkGold={darkGold}
      gapClassName={gapClassName}
    />
  );
};

export default SaveToFavoritesButton;
